/**
 * PixelVerify Component
 * Shows after pixel is saved, guides user to verify it
 */

import React, { useState } from 'react';
import axios from 'axios';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useToast } from '../analytics/Toast';
import './PixelVerify.css';

const PixelVerify = ({ metaInfo, onVerified, onDisconnect }) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const { token } = useAuthToken();
  const { showToast } = useToast();



  const handleVerify = async () => {
    const businessId = localStorage.getItem('user_id');
    if (!businessId) {
      showToast('Business ID not found', 'error');
      return;
    }

    
    if (!token) {
      showToast('Authentication token not available', 'error');
      return;
    }

    try {
      setIsVerifying(true);
      const response = await axios.post(
        `${process.env.REACT_APP_APIURL}meta/pixel/verify`,
        { business_id: businessId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showToast('Pixel verified!', 'success');
        setTimeout(() => {
          if (onVerified) onVerified();
        }, 500);
      } else {
        showToast(response.data.message || 'Verification failed', 'error');
      }
    } catch (error) {
      console.error('[Meta] Verify error:', error);
      showToast(
        error.response?.data?.message || 'Failed to verify pixel',
        'error'
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDisconnect = async () => {
    const businessId = localStorage.getItem('user_id');
    if (!businessId) {
      showToast('Business ID not found', 'error');
      return;
    }

    try {
      setIsDisconnecting(true);
      const response = await axios.delete(
        `${process.env.REACT_APP_APIURL}meta/pixel/remove`,
        {
          data: { business_id: businessId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        showToast('Meta Pixel disconnected', 'success');
        setTimeout(() => {
          if (onDisconnect) onDisconnect();
        }, 500);
      } else {
        showToast(response.data.message || 'Failed to disconnect', 'error');
      }
    } catch (error) {
      console.error('[Meta] Disconnect error:', error);
      showToast(
        error.response?.data?.message || 'Failed to disconnect pixel',
        'error'
      );
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  return (
    <div className="meta-verify-wrapper">
     

      {/* Status Card */}
      <div className="meta-status-card">
        <div className="meta-status-header">
          <div className="meta-brand-icons">
            {/* Facebook */}
            <div className="meta-brand-icon meta-brand-icon-fb">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="#fff" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.994 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </div>
            {/* Instagram */}
            <div className="meta-brand-icon meta-brand-icon-ig">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="#fff" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </div>
          </div>
          <div className="meta-status-info">
            <h3 className="meta-status-name">{metaInfo?.pixelName || 'Meta Pixel'}</h3>
            <p className="meta-status-id">{metaInfo?.pixelId}</p>
          </div>
          <div
            className={`meta-status-badge ${
              metaInfo?.connected ? 'meta-badge-active' : 'meta-badge-pending'
            }`}
          >
            {metaInfo?.connected ? 'Active' : 'Pending'}
          </div>
        </div>

        {metaInfo?.connected && (
          <div className="meta-success-banner">
            <svg viewBox="0 0 24 24">
              <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
            </svg>
            <span>Pixel active — tracking Facebook & Instagram visitors automatically</span>
          </div>
        )}
      </div>

      {/* Steps Card (shown when not verified) */}
      {!metaInfo?.isVerified && (
        <div className="meta-steps-card">
          <h3 className="meta-steps-title">Fire your first event</h3>
          <ol className="meta-steps-list">
            <li className="meta-step-item">
              <span className="meta-step-number">1</span>
              <span>Open your store in a new tab</span>
            </li>
            <li className="meta-step-item">
              <span className="meta-step-number">2</span>
              <span>This fires a PageView event to Meta automatically</span>
            </li>
            <li className="meta-step-item">
              <span className="meta-step-number">3</span>
              <span>Come back and click Verify Pixel below</span>
            </li>
          </ol>
          <div className="meta-steps-tip">
            <svg viewBox="0 0 24 24">
              <path fill="currentColor" d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z" />
            </svg>
            <span>
              Install{' '}
              <a
                href="https://chrome.google.com/webstore/detail/meta-pixel-helper/fdgfkebogiimcoedlicjlajpkdmockpc"
                target="_blank"
                rel="noopener noreferrer"
              >
                Meta Pixel Helper Chrome extension
              </a>{' '}
              to test
            </span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="meta-verify-actions">
        <button
          className="meta-btn-verify"
          onClick={handleVerify}
          disabled={isVerifying}
        >
          {isVerifying ? (
            <>
              <div className="meta-spinner meta-spinner-sm" />
              <span>Verifying...</span>
            </>
          ) : (
            <span>{metaInfo?.isVerified ? 'Re-verify Pixel' : 'Verify Pixel'}</span>
          )}
        </button>

        {metaInfo?.isVerified && (
          <button className="meta-btn-dashboard" onClick={onVerified}>
            <span>Go to Dashboard</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
            </svg>
          </button>
        )}
      </div>

      {/* Disconnect Section */}
      <div className="meta-disconnect-section">
        {!showDisconnectConfirm ? (
          <span
            className="meta-disconnect-link"
            onClick={() => setShowDisconnectConfirm(true)}
          >
            Disconnect Meta Pixel
          </span>
        ) : (
          <div className="meta-disconnect-confirm">
            <span>Remove pixel from your store?</span>
            <button
              className="meta-btn-confirm-yes"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? 'Removing...' : 'Yes, remove'}
            </button>
            <button
              className="meta-btn-confirm-cancel"
              onClick={() => setShowDisconnectConfirm(false)}
              disabled={isDisconnecting}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PixelVerify;
